import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import ConfirmModal from "components/shared-components/ConfirmModal";
import HubMultiSelectFilter, {
  joinMultiSelectIds,
  parseMultiSelectIds,
  useHubMultiSelectOptions,
} from "components/shared-components/HubMultiSelectFilter";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import { ROLE_APP, actionAccess } from "constants/actionAccess";
import jwt_decode from "jwt-decode";

import { GroupTransportMultiFilter } from "components/Common/GroupTransportSelect";
import { ORDER_TYPE, TRANSFER_ORDER_TYPE, OPTION_TRANSFER_ORDER_TYPE, STATUS_FILTER_KEY } from "constants/order";
import { CheckActionAccess, splitText, useQueryRouter } from "helpers/utils";
import {
  formatOrderCostMoney,
  fromOrderCostThousandUnit,
  parseOrderCostMoney,
  toOrderCostThousandUnit,
} from "helpers/orderCostHelper";
import {
  useCancelOrderReasonList,
  useCancelTransferOrder,
  useChangeOrderStatusMultiple,
  useExportOrders,
  useForwardOrderMultiple,
  useListShippingUnitAll,
  useListTransferOrders,
  useRetryOrder,
  useTransferOrderStatus,
  useTypeOrder,
  useUpdateOrderCost,
} from "hooks";
import UpdateRefCodeModal from "pages/ ScanTheRow/UpdateRefCode/UpdateRefCodeModal";
import {
  checkItemAcceptForUpdateExitsRefCode,
  orderStatusMode,
} from "pages/Orders/utils";
import { DELAY_FILTER_TIME } from "constants/RemoteDataTableContanst";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { textFilter } from "react-bootstrap-table2-filter";
import { DateRangePicker } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { useTranslation } from "react-i18next";
import MetaTags from "react-meta-tags";
import { useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import {
  Button,
  Card,
  CardBody,
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  Row,
  Spinner,
} from "reactstrap";
import { AUTH_TOKEN } from "store/auth/login/actionTypes";
import ChangeOrderStatusForm, {
  checkIsAnyNotAllowedGroupToTransfer,
} from "../Orders/OrderDetail/ChangeOrderStatusForm/ChangeOrderStatusForm";
import RefCode from "pages/Orders/RefCode";
import { findOrderType } from "pages/ManageOrders/GroupButtonOrderType";
import OrderCodeCopy from "pages/ManageOrders/OrderCodeCopy";

const ManageTransferOrdersPage = () => {
  let queryRouter = useQueryRouter();
  const updateRefCodeModalRef = useRef(null);
  const initParams = () => {
    return {
      sizePerPage: 10,
      page: 1,
      columnFilter: {},
      keywords: "",
      expand: "from_hub_name,to_hub_name,transport_name",
      run: false,
    };
  };

  const { t } = useTranslation(["myOrders"]);
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const allFilterOption = {
    value: "All",
    label: t("filters.all"),
  };

  /* ================================== START INIT STATE ==================================== */
  const [isLogAdmin, setIsLogAdmin] = useState(false);
  const [isHubAdmin, setIsHubAdmin] = useState(true);
  const [orderParams, setOrderParams] = useState(initParams);
  const [selectedHubIds, setSelectedHubIds] = useState([]);
  const isHubFilterInitializedRef = useRef(false);
  const [columnsTable, setColumnsTable] = useState([]);
  const [actionType, setActionType] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIds, setSelectedIs] = useState([]);
  const [editingCostOrderId, setEditingCostOrderId] = useState(null);
  const [editingCostValue, setEditingCostValue] = useState("");
  const [selectedOrderCode, setSelectedOrderCode] = useState([]);
  const [orderStatusList, setOrderStatusList] = useState([]);
  const [statusListAllOptions, setStatusListAllOptions] = useState([]);
  const [dataCancelOrder, setDataCancelOrder] = useState({
    orderId: null,
  });
  const [cancelOrderModal, setCancelOrderModal] = useState(false);
  const [openChangeOrderStatusPopup, setOpenChangeOrderStatusPopup] =
    useState(false);
  const [selectedTransportCodes, setSelectedTransportCodes] = useState([]);
  const [selectedOrderTypeIds, setSelectedOrderTypeIds] = useState([]);

  /* =================================== END INIT STATE ===================================== */

  /* ===================================== START HOOKS ====================================== */

  const { hubSelectOptions, isLoading: isHubOptionsLoading } =
    useHubMultiSelectOptions({
      queryKey: "manage-transfer-orders-hubs",
    });

  const applyHubFilterToOrderParams = useCallback(
    nextSelectedHubIds => {
      if (!hubSelectOptions.length) {
        return;
      }

      const hubFilterIds = joinMultiSelectIds({
        selectedIds: nextSelectedHubIds,
        options: hubSelectOptions,
      });

      if (!hubFilterIds) {
        return;
      }

      const columnFilterKeep = {
        ...(orderParams.columnFilterKeep || {}),
        hub_id: hubFilterIds,
      };

      setOrderParams(prev => ({
        ...prev,
        page: 1,
        run: true,
        columnFilterKeep,
        columnFilter: {
          ...prev.columnFilter,
          ...columnFilterKeep,
          "filter[transport_id]": undefined,
        },
      }));
    },
    [hubSelectOptions, orderParams.columnFilterKeep]
  );

  useEffect(() => {
    if (
      isHubOptionsLoading ||
      !hubSelectOptions.length ||
      isHubFilterInitializedRef.current
    ) {
      return;
    }

    isHubFilterInitializedRef.current = true;

    const defaultSelectedHubIds = undefined;

    setSelectedHubIds([]);
    applyHubFilterToOrderParams(defaultSelectedHubIds);
  }, [applyHubFilterToOrderParams, hubSelectOptions, isHubOptionsLoading]);

  const handleHubMultiSelectChange = nextHubIds => {
    setSelectedHubIds(Array.isArray(nextHubIds) ? nextHubIds : []);
    applyHubFilterToOrderParams(nextHubIds);
  };

  const isAllowExport = CheckActionAccess(
    actionAccess.GLOG_FUNCTION_ORDER_EXPORT
  );

  // FORWARD ORDER

  const onSuccessForwardOrder = () => {
    refetchListOrders();
  };

  const { mutate: onForwardOrder } = useForwardOrderMultiple(
    onSuccessForwardOrder
  );

  // CHANGE ORDER STATUS

  const onSuccessChangeOrderStatus = () => {
    refetchListOrders();
  };

  const { mutate: changeOrderStatus } = useChangeOrderStatusMultiple(
    onSuccessChangeOrderStatus
  );

  // CANCEL ORDER

  const handleCancelOrderSuccess = () => {
    refetchListOrders();
  };

  const { mutate: cancelOrder } = useCancelTransferOrder(
    handleCancelOrderSuccess
  );

  const { mutate: mutateUpdateOrderCost } = useUpdateOrderCost(
    () => {
      setEditingCostOrderId(null);
      refetchListOrders();
    },
    () => {
      setEditingCostOrderId(null);
    }
  );

  const handleUpdateOrderCost = (orderId, thousandDigits) => {
    const numericCost = fromOrderCostThousandUnit(thousandDigits);
    mutateUpdateOrderCost({
      orderId,
      cost_value: numericCost,
    });
  };

  // REASONS

  const { refetch: onFetchOrderReason, data: reasonListData } =
    useCancelOrderReasonList(1);

  const handleTransportGroupMultiChange = nextCodes => {
    const safeCodes = Array.isArray(nextCodes) ? nextCodes : [];
    setSelectedTransportCodes(safeCodes);

    setOrderParams(prev => ({
      ...prev,
      page: 1,
      run: true,
      columnFilter: {
        ...prev.columnFilter,
        "filter[transport_id]": undefined,
        transport_group_code: safeCodes.length
          ? safeCodes.join(",")
          : undefined,
      },
    }));
  };

  // SHIPPING TYPE ALL

  const onFetchShippingTypeAllSuccess = response => {
    const shippingTypeAll = [];
    for (const key in response) {
      shippingTypeAll.push({ value: key, label: response[key] });
    }
  };

  const { refetch: listShippingTypeAll } = useListShippingUnitAll(
    onFetchShippingTypeAllSuccess
  );

  // ORDER STATUS

  const onFetchOrderStatusSuccess = response => {
    // Set default status query filter
    const queryStatus = queryRouter.get("statuses")?.split(",");
    setOrderStatusList(response);
    setStatusListAllOptions([allFilterOption, ...response]);
  };

  const { refetch: onFetchOrderStatus } = useTransferOrderStatus(
    onFetchOrderStatusSuccess
  );

  // EXPORT ORDER
  const exportOrderParams = {
    params: {
      ...orderParams,
      columnFilter: {
        ...orderParams.columnFilter,
        "filter[id][in]": selectedIds,
      },
    },
  };

  const onExportSuccess = data => {
    if (data) {
      const outputFilename = `ExportFile-${Date.now()}.xlsx`;
      const url = URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const { isLoading: isExportingFile, refetch: exportOrders } = useExportOrders(
    exportOrderParams,
    onExportSuccess
  );

  // ORDERS

  const {
    data: orderList,
    isFetching: isLoadingListOrder,
    refetch: refetchListOrders,
  } = useListTransferOrders({
    params: orderParams,
  });

  const { data: listOrderType } = useTypeOrder();

  useEffect(() => {
    const queryType = queryRouter.get("type");
    const initOrderTypeIds = queryType
      ? queryType.split(",").map(v => v.trim()).filter(Boolean)
      : [];
    setSelectedOrderTypeIds(initOrderTypeIds);

    setOrderParams(prev => ({
      ...prev,
      page: 1,
      sizePerPage: 10,
      columnFilter: {
        ...prev.columnFilter,
        "status": queryRouter
          .get("statuses")
          ?.split(",")
          .map(item => item.trim())
          .filter(Boolean)
          .join(","),
        ...(queryType ? { "filter[ordertype]": queryType } : {}),
        dashboard_key: queryRouter.get("key"),
      },
    }));
    onFetchOrderStatus();
  }, []);

  useEffect(() => {
    if (orderParams && orderParams.run) {
      refetchListOrders();
    }
  }, [orderParams]);

  useEffect(() => {
    if (orderList) {
      setColumnsTable(initColumns);
      setSelectedIs([]);
    }
  }, [orderList]);

  useEffect(() => {
    setColumnsTable(initColumns);
  }, [orderStatusList]);

  useEffect(() => {
    setColumnsTable(initColumns);
  }, [editingCostOrderId, editingCostValue, listOrderType]);

  /* ===================================== END HOOKS ======================================== */

  /* ================================ START FUNCTION HANDLER ================================ */

  const handleChangeOrderStatus = data => {
    const orderParams = { ...data };
    orderParams.ids = selectedIds;
    changeOrderStatus(orderParams);
  };

  const isAnyNotAllowedGroupToTransfer = useMemo(() => {
    return checkIsAnyNotAllowedGroupToTransfer(orderList, selectedIds);
  }, [selectedIds, orderList]);

  const formatStatus = cell => {
    const status = statusListAllOptions.find(
      item => parseInt(item.value) === cell
    );
    return <div>{status?.label}</div>;
  };

  const clickViewOrderHandler = row => {
    navigate(
      { pathname: `/manage-transfer-orders/${row.id}` },
      {
        state: {
          allowEdit: false,
        },
      }
    );
  };

  const clickEditOrderHandler = row => {
    navigate(
      { pathname: `/manage-transfer-orders/${row.id}` },
      {
        state: {
          allowEdit: true,
        },
      }
    );
  };

  const clickCopyOrderHandler = row => {
    window.open(`/manage-transfer-orders/copy/${row.id}`, "_blank");
  };

  const rightClickViewOrderHandler = (row, indexCol, e) => {
    if (e && (e.button === 1 || e.button === 2)) {
      window.open(
        `/manage-transfer-orders/${row.id}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const toggleCancelOrderModal = () => {
    setCancelOrderModal(!cancelOrderModal);
  };

  const clickCancelOrderHandler = row => {
    setSelectedRow(row);
    setDataCancelOrder({ orderId: row.id });
    toggleCancelOrderModal();
    onFetchOrderReason();
  };

  const cancelSingleOrderHandler = value => {
    if (value.reasonId) {
      cancelOrder({
        orderId: dataCancelOrder.orderId,
        reason_note: value.note,
        reason_id: value.reasonId,
        order_code: selectedRow?.["order_code"],
      });
      toggleCancelOrderModal();
    }
  };

  const onSetDaySize = () => {
    if (window.matchMedia("(max-width: 560px)").matches) {
      return 22;
    }
    if (window.matchMedia("(max-width: 800px)").matches) {
      return 24;
    }
    return undefined;
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter: tableColumnFilter }
  ) => {
    setOrderParams(prev => {
      const nextColumnFilter = {
        ...prev.columnFilter,
        ...(tableColumnFilter || {}),
      };

      delete nextColumnFilter["filter[delay_time][like]"];

      if (!tableColumnFilter?.delay_time) {
        delete nextColumnFilter.delay_time;
      }

      return {
        ...prev,
        sort: sortField ? sortOrder + sortField : prev.sort,
        columnFilter: nextColumnFilter,
        page: page || 1,
        sizePerPage: sizePerPage || 10,
      };
    });
  };

  const handleRowsSelect = (ids, row) => {
    setSelectedIs(ids);
    setSelectedOrderCode(row.map(item => item.order_code));
  };

  const statusSelectOptions = useMemo(
    () =>
      (orderStatusList || []).map(item => ({
        value: String(item.value),
        label: item.label,
      })),
    [orderStatusList]
  );

  const selectedStatusIds = useMemo(() => {
    const selectedStatusIdsFromFilter = parseMultiSelectIds(
      orderParams.columnFilter?.[STATUS_FILTER_KEY]
    );

    if (!selectedStatusIdsFromFilter.length || !statusSelectOptions.length) {
      return [];
    }

    const allStatusIds = joinMultiSelectIds({
      selectedIds: undefined,
      options: statusSelectOptions,
    });

    if (selectedStatusIdsFromFilter.join(",") === allStatusIds) {
      return [];
    }

    return selectedStatusIdsFromFilter;
  }, [orderParams.columnFilter, statusSelectOptions]);

  const handleStatusMultiSelectChange = useCallback(
    statuses => {
      const statusFilterIds = joinMultiSelectIds({
        selectedIds: statuses,
        options: statusSelectOptions,
      });

      setOrderParams(prev => {
        const columnFilter = { ...prev.columnFilter };

        if (statusFilterIds) {
          columnFilter[STATUS_FILTER_KEY] = statusFilterIds;
        } else {
          delete columnFilter[STATUS_FILTER_KEY];
        }

        return {
          ...prev,
          page: 1,
          run: true,
          columnFilter,
        };
      });
    },
    [statusSelectOptions]
  );

  const orderTypeSelectOptions = useMemo(
    () =>
      (OPTION_TRANSFER_ORDER_TYPE || []).map(item => ({
        value: String(item.value),
        label: item.label,
      })),
    []
  );

  const handleOrderTypeMultiChange = useCallback(
    nextIds => {
      const safeIds = Array.isArray(nextIds) ? nextIds : [];
      setSelectedOrderTypeIds(safeIds);

      const orderTypeFilterIds = joinMultiSelectIds({
        selectedIds: safeIds.length ? safeIds : undefined,
        options: orderTypeSelectOptions,
      });

      setOrderParams(prev => {
        const columnFilter = { ...prev.columnFilter };

        if (orderTypeFilterIds) {
          columnFilter["filter[ordertype]"] = orderTypeFilterIds;
        } else {
          delete columnFilter["filter[ordertype]"];
        }

        return {
          ...prev,
          page: 1,
          run: true,
          columnFilter,
        };
      });
    },
    [orderTypeSelectOptions]
  );

  const onSearchAllHandle = value => {
    setOrderParams(prev => ({
      ...prev,
      keywords: value,
    }));
  };

  const handleDateRangeFilter = ({ startDate, endDate }) => {
    const columnFilter = { ...orderParams.columnFilter };

    setStartDate(startDate);
    setEndDate(endDate);

    delete columnFilter["filter[action_date][gte]"];
    delete columnFilter["filter[action_date][lte]"];
    delete columnFilter["filter[maker_date][gte]"];
    delete columnFilter["filter[maker_date][lte]"];
    delete columnFilter["filter[action_date][eq]"];
    delete columnFilter["filter[maker_date][eq]"];
    let keyFilter = "maker_date";
    if (startDate && endDate) {
      columnFilter[`filter[${keyFilter}][gte]`] = startDate
        ? startDate.format("YYYY-MM-DD")
        : null;
      columnFilter[`filter[${keyFilter}][lte]`] = endDate
        ? endDate.format("YYYY-MM-DD")
        : null;
    } else if (startDate) {
      columnFilter[`filter[${keyFilter}][eq]`] = startDate
        ? startDate.format("YYYY-MM-DD")
        : null;
    } else if (endDate) {
      columnFilter[`filter[${keyFilter}][eq]`] = endDate
        ? endDate.format("YYYY-MM-DD")
        : null;
    }
    setOrderParams(prev => ({
      ...prev,
      columnFilter,
    }));
  };

  const handleExportFile = () => {
    if (selectedIds.length === 0) {
      addToast(t("message.not_selected_information"), {
        appearance: "warning",
        autoDismiss: true,
      });
      return;
    }
    exportOrders();
  };

  const handleChangeStatus = () => {
    if (selectedIds && selectedIds.length > 0) {
      setOpenChangeOrderStatusPopup(!openChangeOrderStatusPopup);
    } else {
      addToast(t("message.not_selected_information"), {
        appearance: "warning",
        autoDismiss: true,
      });
    }
  };

  const onFetchRetryOrderSuccess = response => {
    refetchListOrders();
  };
  const onFetchRetryOrderFail = response => { };

  const { mutate: mutateRetryOrder } = useRetryOrder(
    onFetchRetryOrderSuccess,
    onFetchRetryOrderFail
  );

  const handleSendBackDVVC = () => {
    if (selectedOrderCode && selectedOrderCode.length > 0) {
      mutateRetryOrder(selectedOrderCode);
    } else {
      addToast(t("message.not_selected_information"), {
        appearance: "warning",
        autoDismiss: true,
      });
    }
  };

  /* ================================ END FUNCTION HANDLER ================================= */

  const initColumns = [
    {
      dataField: "address_type",
      text: "Loại",
      sort: false,
      formatter: (_cell, row) => {
        const isRowLevel2 = row?.is_new_address === "N";
        const isEditing = editingCostOrderId === row.id;
        const displayCost = row.cost
          ? formatOrderCostMoney(toOrderCostThousandUnit(row.cost))
          : "";
        return (
          <div
            style={{ minWidth: 90, cursor: "pointer" }}
            onClick={() => {
              setEditingCostOrderId(row.id);
              setEditingCostValue(
                row.cost ? String(toOrderCostThousandUnit(row.cost)) : ""
              );
            }}
            title="Click để nhập chi phí"
          >
            <span
              className={`badge ${isRowLevel2 ? "bg-info" : "bg-secondary"}`}
              style={{
                fontSize: "11px",
                padding: "4px 8px",
                textAlign: "center",
                display: "inline-block",
              }}
            >
              {isRowLevel2 ? "2 cấp" : "3 cấp"}
            </span>
            {isEditing && (
              <input
                autoFocus
                type="text"
                inputMode="numeric"
                className="form-control form-control-sm mt-1"
                value={formatOrderCostMoney(editingCostValue)}
                onChange={e => setEditingCostValue(parseOrderCostMoney(e.target.value))}
                onBlur={() => setEditingCostOrderId(null)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    const currentDigits = parseOrderCostMoney(editingCostValue);
                    const nextCost = fromOrderCostThousandUnit(currentDigits);
                    const prevCost = Number(row.cost || 0);

                    if (nextCost === prevCost) {
                      setEditingCostOrderId(null);
                      return;
                    }

                    handleUpdateOrderCost(row.id, currentDigits);
                  }

                  if (e.key === "Escape") {
                    setEditingCostOrderId(null);
                  }
                }}
                placeholder={displayCost || "Nhập số tiền"}
              />
            )}
          </div>
        );
      },
    },
    {
      dataField: "maker_date",
      text: "Thời gian tạo",
      sort: true,
      formatter: (cell, row) => {
        if (cell) {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
            .format(Number(cell) * 1000)
            .replace(",", "");
        } else {
          return "";
        }
      },
    },
    // {
    //   dataField: "delay_time",
    //   text: t("table.time"),
    //   sort: true,
    //   formatter: cell => {
    //     return cell;
    //   },
    // },
    {
      dataField: "delay_time",
      text: "DU",
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        className: "form-control-sm",
        placeholder: "Nhập DU",
        style: { width: "65px", minWidth: "65px", maxWidth: "65px", height: 34 },
        onInput: e => e.target.value = e.target.value.replace(/\D/g, "")
      }),
      formatter: cell => {
        if (!cell) return "";
        return String(cell).replace(/\s*ngày\s*/gi, " D");
      },
    },
    {
      dataField: "type",
      text: t("table.type"),
      sort: true,
      formatter: cell => {
        return findOrderType(cell, listOrderType)?.label;
      },
    },
    {
      dataField: "order_code",
      text: t("table.id"),
      sort: true,
      formatter: (cell, row) => <OrderCodeCopy key={row.id} code={cell} />,
    },
    {
      dataField: "ref_code",
      text: t("table.package_code"),
      sort: true,
      formatter: (cell, row) => {
        const isAllowUpdateRefCode = isLogAdmin || isHubAdmin;
        const isAllowUpdateExistRefCode =
          isAllowUpdateRefCode && checkItemAcceptForUpdateExitsRefCode(row);
        return (
          <RefCode
            t={t}
            ref_code={cell}
            data={row}
            isAllowUpdateRefCode={isAllowUpdateRefCode}
            onUpdateRefCode={() =>
              updateRefCodeModalRef?.current?.openModal({
                orderId: row?.id,
                orderRefCode: row.ref_code,
              })
            }
            isAllowUpdateExistRefCode={isAllowUpdateExistRefCode}
            onUpdateExistRefCode={() =>
              updateRefCodeModalRef?.current?.openModal({
                orderId: row?.id,
                orderRefCode: row.ref_code,
                isUpdateExist: true,
              })
            }
          />
        );
      },
    },
    {
      dataField: "from_hub_name",
      text: "Nơi gửi",
      formatter: cell => {
        return <div className="text-info fw-bold">{cell}</div>;
      },
    },
    {
      dataField: "to_hub_name",
      text: "Nơi nhận",
      formatter: cell => {
        return <div className="text-success fw-bold">{cell}</div>;
      },
    },
    {
      dataField: "status",
      text: t("table.status"),
      sort: true,
      formatter: cell => {
        return formatStatus(cell);
      },
    },
    {
      dataField: "sender_contact_phone",
      text: "SĐT nơi nhận",
      sort: true,
      formatter: (cell, row) => {
        return row.reciever_contact_phone || row.reciever_contact_phone2;
      },
    },
    {
      dataField: "address",
      text: "Địa chỉ nơi nhận",
      sort: true,
      formatter: (cell, row) => (
        <div
          dangerouslySetInnerHTML={{
            __html: splitText(row.reciever_full_address, [
              {
                limit_width: 3000,
                limit_text_length: 50,
              },
              {
                limit_width: 600,
                limit_text_length: 30,
              },
            ]),
          }}
        />
      ),
    },
    {
      dataField: "transport_name",
      text: t("table.transport_id"),
      sort: true,
    },
    {
      dataField: "time_slot_name",
      text: t("table.timeslot_id"),
      sort: true,
      formatter: (cell, row) => {
        return cell;
      },
    },
  ];

  const listRowAction = [
    {
      // actionAccess: actionAccess.GLOG_FUNCTION_ORDER_VIEW,
      icon: <i className="fas fa-eye fa-lg text-primary ms-2" />,
      text: t("actions.view"),
      onClick: clickViewOrderHandler,
      onRightClick: rightClickViewOrderHandler,
    },
    // START Đơn nhận mẫu
    {
      isShow: isHubAdmin,
      icon: <i className="fas fa-pen fa-lg text-success ms-2" />,
      text: t("actions.edit"),
      onClick: clickEditOrderHandler,
      conditionVisible: {
        status: [orderStatusMode.COORDINATED, orderStatusMode.MAKE], // HubAdmin Thêm và xóa đơn con ở trạng thái "Tạo đơn" ,"Đã điều phối"
        type: [TRANSFER_ORDER_TYPE.RECEIVE],
      },
    },
    {
      isShow: isLogAdmin,
      icon: <i className="fas fa-pen fa-lg text-success ms-2" />,
      text: t("actions.edit"),
      onClick: clickEditOrderHandler,
      conditionVisible: {
        status: Object.values(orderStatusMode), // LogAdmin Thêm và xóa đơn con ở tất cả trạng thái
        type: [TRANSFER_ORDER_TYPE.RECEIVE],
      },
    },
    // END Đơn nhận mẫu
    // START Đơn Trả kết quả
    {
      isShow: isLogAdmin,
      icon: <i className="fas fa-pen fa-lg text-success ms-2" />,
      text: t("actions.edit"),
      onClick: clickEditOrderHandler,
      conditionVisible: {
        status: Object.values(orderStatusMode)?.filter(
          s => s !== orderStatusMode.COMPLETED
        ), // LogAdmin Thêm và xóa đơn con ở tất cả trạng thái trừ "Hoàn tất"
        type: [TRANSFER_ORDER_TYPE.RETURN],
      },
    },
    // END Đơn Trả kết quả
    {
      isShow: isLogAdmin || isHubAdmin,
      icon: <i className="fas fa-times fa-lg ms-2 text-danger" />,
      text: t("actions.cancel"),
      onClick: clickCancelOrderHandler,
      conditionVisible: {
        status: [orderStatusMode.MAKE, orderStatusMode.WAITING], // Tạo đơn, Đang chờ
      },
    },
    {
      isShow: isLogAdmin || isHubAdmin,
      icon: <i className="fas fa-file-import fa-lg ms-2 text-warning" />,
      text: t("actions.copy"),
      onClick: clickCopyOrderHandler,
      conditionVisible: {
        ref_code: [null, undefined, ""],
        status: [orderStatusMode.MAKE, orderStatusMode.WAITING], // Tạo đơn, Đang chờ
      },
    },
  ];

  const selectRow = {
    mode: "checkbox",
    // clickToSelect: true,
    bgColor: "#00BFFF",
    onSelect: handleRowsSelect,
  };

  useEffect(() => {
    const info = jwt_decode(localStorage.getItem(AUTH_TOKEN));
    const listRole = info["auth"].split(",");
    setIsHubAdmin(
      listRole.find(item => item.includes(ROLE_APP.ROLE_HUB_ADMIN))
        ? true
        : false || false
    );
    setIsLogAdmin(
      listRole.find(item => item.includes(ROLE_APP.ROLE_LOG_ADMIN))
        ? true
        : false || false
    );
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Modal
          isOpen={openChangeOrderStatusPopup}
          centered
          toggle={() =>
            setOpenChangeOrderStatusPopup(!openChangeOrderStatusPopup)
          }
        >
          <ChangeOrderStatusForm
            orderStatusList={orderStatusList}
            handleCancel={() => {
              setOpenChangeOrderStatusPopup(false);
            }}
            onSubmit={handleChangeOrderStatus}
            isAnyNotAllowedGroupToTransfer={isAnyNotAllowedGroupToTransfer}
            placeHolderNotAllowedGroupToTransfer="Không thể chọn vì nhóm đối tượng có tồn tại đơn nội thành"
          />
        </Modal>
        <MetaTags>
          <title>Manage Orders</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={
              t("title.transferOrder") + ` (${orderList?.totalCount || 0})`
            }
          />
          <Row>
            {/* Modal START*/}
            <Col className="col-12">
              <UpdateRefCodeModal
                ref={updateRefCodeModalRef}
                handleOnSuccessInsertOrderRefCode={() => {
                  refetchListOrders();
                }}
              />
              <ConfirmModal
                isOpen={cancelOrderModal}
                titleHeader={
                  t("actions.deleteModal.title") +
                  `${selectedRow?.["order_code"] || ""}` +
                  "?"
                }
                titleBody={t("actions.deleteModal.content")}
                onConfirm={cancelSingleOrderHandler}
                textBtnConfirm={t("actions.deleteModal.confirmBtn")}
                textBtnCancel={t("actions.deleteModal.cancelBtn")}
                toggle={toggleCancelOrderModal}
                isReasonDeleteCheckbox={true}
                reasonList={reasonListData?.items}
                isHaveNote={true}
              />
            </Col>

            {/* Modal E N D*/}
            <Col className="col-12">
              <Card>
                <CardBody>
                  <RemoteDataTable
                    isShowRowIndex={false}
                    keyField="id"
                    data={orderList?.items || []}
                    columns={columnsTable}
                    selectRow={selectRow}
                    page={orderList?.params?.page}
                    sizePerPage={orderList?.params?.sizePerPage}
                    totalSize={orderList?.totalCount}
                    onTableChange={handleTableChange}
                    onSearchAllHandle={onSearchAllHandle}
                    // listRowAction={rowActionList}
                    listRowAction={listRowAction}
                    onRowsSelect={handleRowsSelect}
                    loading={isLoadingListOrder}
                    searchSticky={true}
                    sizePerPageListAppend={[
                      {
                        text: "100",
                        value: 100,
                      },
                    ]}
                  >
                    <div className="d-flex flex-column flex-md-row flex-grow-0 flex-lg-grow-1 flex-md-wrap justify-content-md-between">
                      <div
                        className="d-flex flex-column flex-sm-wrap flex-sm-row mb-md-0"
                        style={{ gap: "0.5rem" }}
                      >
                        {/* <Label
                          className="d-flex align-items-center gap-1 mb-0"
                          style={{ cursor: "pointer" }}
                          htmlFor="is_level_2"
                          onClick={() => handleChangeIsLevel2(!is_level_2)}
                        >
                          <Switch
                            checked={is_level_2}
                            onChange={handleChangeIsLevel2}
                          />
                          Địa chỉ 2 cấp
                        </Label> */}
                        <div>
                          <DateRangePicker
                            numberOfMonths={1}
                            startDate={startDate}
                            startDateId="s_id"
                            endDate={endDate}
                            endDateId="e_id"
                            startDatePlaceholderText={t("title.startDate")}
                            endDatePlaceholderText={t("title.endDate")}
                            isOutsideRange={() => false}
                            onDatesChange={handleDateRangeFilter}
                            focusedInput={focusedInput}
                            onFocusChange={e => setFocusedInput(e)}
                            displayFormat="DD/MM/YYYY"
                            daySize={onSetDaySize()}
                            orientation={
                              window.matchMedia("(max-width: 700px)").matches
                                ? "vertical"
                                : "horizontal"
                            }
                          />
                        </div>
                        <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
                          <div
                            className={
                              window.matchMedia("(max-width: 575px)").matches
                                ? "w-50"
                                : ""
                            }
                          >
                            <HubMultiSelectFilter
                              label="Hub"
                              hubSelectOptions={hubSelectOptions}
                              selectedHubIds={selectedHubIds}
                              onChange={handleHubMultiSelectChange}
                            />
                          </div>
                          <div
                            className={
                              window.matchMedia("(max-width: 575px)").matches
                                ? "w-50"
                                : ""
                            }
                          >
                            <HubMultiSelectFilter
                              label={t("filters.status.label")}
                              hubSelectOptions={statusSelectOptions}
                              selectedHubIds={selectedStatusIds}
                              onChange={handleStatusMultiSelectChange}
                            />
                          </div>
                          <div
                            className={
                              window.matchMedia("(max-width: 575px)").matches
                                ? "w-50"
                                : ""
                            }
                          >
                            <GroupTransportMultiFilter
                              label="ĐVVC"
                              selectedCodes={selectedTransportCodes}
                              onChange={handleTransportGroupMultiChange}
                            />
                          </div>
                          <div
                            className={
                              window.matchMedia("(max-width: 575px)").matches
                                ? "w-50"
                                : ""
                            }
                          >
                            <HubMultiSelectFilter
                              label="Loại đơn"
                              hubSelectOptions={orderTypeSelectOptions}
                              selectedHubIds={selectedOrderTypeIds}
                              onChange={handleOrderTypeMultiChange}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-md-end">
                        {isAllowExport && (
                          <div className="ms-2">
                            <Dropdown
                              isOpen={actionType}
                              toggle={() => setActionType(!actionType)}
                            >
                              <DropdownToggle
                                className="btn primary-btn w-100"
                                caret
                              >
                                {"Tác vụ"}{" "}
                                <i className="mdi mdi-chevron-down" />{" "}
                                {!!isExportingFile && (
                                  <Spinner
                                    // style={{ marginLeft: "5px" }}
                                    color="white"
                                    size="sm"
                                  />
                                )}
                              </DropdownToggle>
                              <DropdownMenu>
                                <DropdownItem onClick={handleExportFile}>
                                  {t("actions.exportFile")}{" "}
                                </DropdownItem>
                                <DropdownItem onClick={handleChangeStatus}>
                                  {t("actions.change_status")}{" "}
                                </DropdownItem>
                                <DropdownItem onClick={handleSendBackDVVC}>
                                  {t("actions.send_back_dvvc")}{" "}
                                </DropdownItem>
                              </DropdownMenu>
                            </Dropdown>
                          </div>
                        )}
                      </div>
                    </div>
                  </RemoteDataTable>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ManageTransferOrdersPage;
